import {
  ChangeDetectionStrategy,
  Component,
  forwardRef,
  Input,
  OnDestroy,
  OnInit,
} from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import {
  ControlValueAccessor,
  FormControl,
  NG_VALUE_ACCESSOR,
  ReactiveFormsModule,
} from '@angular/forms';
import { InputWithIconModule } from '@epicuro-next/ataraxia/input-with-icon';
import { FontAwesomeModule } from '@fortawesome/angular-fontawesome';
import { Subject } from 'rxjs';
import { map, takeUntil } from 'rxjs/operators';

@Component({
  selector: 'atx-search-input',
  imports: [FontAwesomeModule, InputWithIconModule, ReactiveFormsModule],
  templateUrl: './search-input.component.html',
  styleUrls: ['./search-input.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => SearchInputComponent),
      multi: true,
    },
  ],
})
export class SearchInputComponent
  implements OnInit, OnDestroy, ControlValueAccessor
{
  @Input() public noResult = false;
  @Input() public isLoading = false;
  @Input() public debounceTime = 1600;
  @Input()
  public placeholderText = $localize`:searchInput|Placeholder text for the search field@@searchInputDefaultPlaceholder:Search`;

  protected searchControl = new FormControl<string>('');

  private destroy$ = new Subject<void>();

  public showClearIcon = toSignal(
    this.searchControl.valueChanges.pipe(map(Boolean)),
    { initialValue: false },
  );

  public ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  public ngOnInit(): void {
    this.searchControl.valueChanges
      .pipe(takeUntil(this.destroy$))
      .subscribe((searchText) => {
        this.onChange(searchText ?? '');
      });
  }

  // eslint-disable-next-line @typescript-eslint/no-empty-function
  public onChange: (arg: string) => void = () => {};

  // eslint-disable-next-line @typescript-eslint/no-empty-function
  public onTouch: () => void = () => {};

  public registerOnChange(fn: never): void {
    this.onChange = fn;
  }

  public registerOnTouched(fn: never): void {
    this.onTouch = fn;
  }

  public writeValue(value: string): void {
    this.searchControl.setValue(value, { emitEvent: false });
  }
}
